import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"

import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import {
  FilteredFloorActionHandlers,
  useFloorActionHandlers
} from "features/floor-management/model/useFloorActionHandlers"
import { FloorFormTabType } from "features/floor-management/types/FloorFormTabType"
import { useProjectStore } from "features/project-management/model/useProjectStore"

import { FloorTabRendererProps } from "../ProjectPageFloorTabConnector"

export interface FloorTabModel {
  handlers: FilteredFloorActionHandlers
  onOpenDetails: () => void
}

export function useFloorTabModel({
  projectID,
  floor,
  onSelectFloor
}: FloorTabRendererProps): FloorTabModel {
  const { isDesktop } = useScreenSize()
  const { permissions } = useProjectPermissions({ projectID })
  const { enterSidebarEditing, setSidebarActiveTab } =
    useProjectStore(projectID)

  const handlers = useFloorActionHandlers({
    projectID,
    floorID: floor.id,
    permissions: permissions ?? readonlyProjectPermissions
  })

  const handleOpenDetails = useEvent(() => onSelectFloor(floor.id))

  const handleEdit = useEvent(() => {
    if (isDesktop) {
      onSelectFloor(floor.id)
      enterSidebarEditing()
    } else {
      handlers.onEdit?.()
    }
  })

  const handleAssignAttachments = useEvent(() => {
    if (isDesktop) {
      onSelectFloor(floor.id)
      enterSidebarEditing()
      setSidebarActiveTab(FloorFormTabType.ATTACHMENTS)
    } else {
      handlers.onAssignAttachments?.()
    }
  })

  const handleAddClarificationRequest = useEvent(() => {
    if (isDesktop) {
      onSelectFloor(floor.id)
      enterSidebarEditing()
      setSidebarActiveTab(FloorFormTabType.CLARIFICATION_REQUEST)
    } else {
      handlers.onAddClarificationRequest?.()
    }
  })

  return {
    handlers: {
      ...handlers,
      onEdit: handlers.onEdit ? handleEdit : undefined,
      onAssignAttachments: handlers.onAssignAttachments
        ? handleAssignAttachments
        : undefined,
      onAddClarificationRequest: handlers.onAddClarificationRequest
        ? handleAddClarificationRequest
        : undefined
    },
    onOpenDetails: handleOpenDetails
  }
}
